import { faChevronLeft, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, CardLink, FormGroup, Input, Label, Spinner, } from "reactstrap";
import { Dispatch } from "redux";
import { releaseNotificationAction } from "../../actions/releaseNotificationAction";
import { DABButton, DABTextBox, LoadingSpinnerModal, } from "../../components/dab";
import { showAlert } from "../common/functions";

// The banner close button and the action labels are not editable, the API always receives these values.
const STATIC_SETTINGS = {
	showBannerCloseButton: true,
	updateNowLabel: "Update Now",
	remindLaterLabel: "Remind Me Later",
	dismissLabel: "Dismiss",
	revision: 0,
};

const TITLE_MAX_LENGTH = 100;
const MODAL_BODY_MAX_LENGTH = 300;
const BANNER_BODY_MAX_LENGTH = 150;
const STORE_URL_MAX_LENGTH = 500;
const WHITESPACE_ONLY_MESSAGE = "Input cannot be only spaces";

// A textarea does not support the pattern attribute, so the whitespace only check
// used by the titles is applied through the constraint validation API instead.
const setWhitespaceValidity = (
	element: HTMLTextAreaElement | null,
	value: string
) => {
	if (!element) {
		return;
	}
	element.setCustomValidity(value && !value.trim() ? WHITESPACE_ONLY_MESSAGE : "");
};

interface ReleaseNotificationProps {
	dispatch: Dispatch<any>;
	releaseNotificationData: any;
}

const ReleaseNotificationDetail: React.SFC<ReleaseNotificationProps> = ({
	dispatch,
	releaseNotificationData,
}) => {
	const history = useHistory();

	const [title, setTitle] = useState("");
	const [body, setBody] = useState("");
	const [minAppVersion, setMinAppVersion] = useState("");
	const [targetApple, setTargetApple] = useState(true);
	const [targetAndroid, setTargetAndroid] = useState(false);
	const [targetAmazon, setTargetAmazon] = useState(false);
	const [modalEnabled, setModalEnabled] = useState(true);
	const [bannerEnabled, setBannerEnabled] = useState(true);
	const [bannerTitle, setBannerTitle] = useState("");
	const [bannerBody, setBannerBody] = useState("");
	const [showRemindLater, setShowRemindLater] = useState(false);
	const [showDismiss, setShowDismiss] = useState(false);
	const [appleStoreUrl, setAppleStoreUrl] = useState("");
	const [googlePlayUrl, setGooglePlayUrl] = useState("");
	const [amazonStoreUrl, setAmazonStoreUrl] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formSubmit, setFormSubmit] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [isDetailLoaded, setIsDetailLoaded] = useState<boolean>(false);
	const [showPlatformError, setShowPlatformError] = useState(false);

	const bodyRef = React.useRef<HTMLTextAreaElement>(null);
	const bannerBodyRef = React.useRef<HTMLTextAreaElement>(null);

	// "All Platforms" is a UI only convenience checkbox, it is never stored or sent to the API.
	const allPlatforms = targetApple && targetAndroid && targetAmazon;
	// At least one store must be targeted, otherwise the notification reaches nobody.
	const hasTargetPlatform = targetApple || targetAndroid || targetAmazon;

	React.useEffect(() => {
		setShowModal(true);
		dispatch(releaseNotificationAction.releaseNotificationGetSetting());
	}, []);

	React.useEffect(() => {
		setWhitespaceValidity(bodyRef.current, body);
	}, [body]);

	React.useEffect(() => {
		setWhitespaceValidity(bannerBodyRef.current, bannerBody);
	}, [bannerBody]);

	React.useEffect(() => {
		if (
			!isDetailLoaded &&
			releaseNotificationData?.isDetailComplete &&
			releaseNotificationData?.settingInput
		) {
			const detail =
				releaseNotificationData.settingInput?.setting ??
				releaseNotificationData.settingInput;
			setTitle(detail?.title ?? "");
			setBody(detail?.body ?? "");
			setMinAppVersion(detail?.minAppVersion ?? "");
			setTargetApple(detail?.targetApple ?? true);
			setTargetAndroid(detail?.targetAndroid ?? false);
			setTargetAmazon(detail?.targetAmazon ?? false);
			const isBannerEnabled = detail?.bannerEnabled ?? true;
			setModalEnabled(detail?.modalEnabled ?? true);
			setBannerEnabled(isBannerEnabled);
			setBannerTitle(detail?.bannerTitle ?? "");
			setBannerBody(detail?.bannerBody ?? "");
			setShowRemindLater(detail?.showRemindLater ?? false);
			setShowDismiss(isBannerEnabled ? detail?.showDismiss ?? false : false);
			setAppleStoreUrl(detail?.appleStoreUrl ?? "");
			setGooglePlayUrl(detail?.googlePlayUrl ?? "");
			setAmazonStoreUrl(detail?.amazonStoreUrl ?? "");
			setIsDetailLoaded(true);
			setShowModal(false);
		}
	}, [releaseNotificationData?.isDetailComplete]);

	if (releaseNotificationData) {
		if (releaseNotificationData.isError && !releaseNotificationData.isSuccess) {
			if (showModal) {
				setShowModal(false);
			}
			if (isSubmitting) {
				setIsSubmitting(false);
			}
			if (formSubmit) {
				setFormSubmit(false);
			}
			releaseNotificationData.isError = false;
			if (releaseNotificationData.status === "Unauthorized") {
				sessionStorage.setItem(
					"fromPath",
					window.location.pathname + window.location.search
				);
				history.push("/401");
			} else {
				showAlert(
					releaseNotificationData.status ||
						"Unable to load the release notification settings.",
					true
				);
			}
		}

		if (
			showModal &&
			formSubmit &&
			!releaseNotificationData.isFormSubmit &&
			releaseNotificationData.isSuccess
		) {
			setShowModal(false);
			setIsSubmitting(false);
			setFormSubmit(false);
			showAlert(releaseNotificationData.status, false);
		}
	}

	const handleAllPlatformsChange = (isChecked: boolean) => {
		setTargetApple(isChecked);
		setTargetAndroid(isChecked);
		setTargetAmazon(isChecked);
		if (isChecked) {
			setShowPlatformError(false);
		}
	};

	// "Show Dismiss" is a banner only action, turning the banner off turns it off as well.
	const handleBannerEnabledChange = (isEnabled: boolean) => {
		setBannerEnabled(isEnabled);
		if (!isEnabled) {
			setShowDismiss(false);
		}
	};

	const handlePlatformChange = (
		setPlatform: (value: boolean) => void,
		isChecked: boolean
	) => {
		setPlatform(isChecked);
		if (isChecked) {
			setShowPlatformError(false);
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		// Blocks a second request while the update is still in progress.
		if (isSubmitting) {
			return;
		}

		if (!hasTargetPlatform) {
			setShowPlatformError(true);
			return;
		}

		const data = {
			setting: {
				title: title,
				body: body,
				minAppVersion: minAppVersion,
				targetApple: targetApple,
				targetAndroid: targetAndroid,
				targetAmazon: targetAmazon,
				modalEnabled: modalEnabled,
				bannerEnabled: bannerEnabled,
				bannerTitle: bannerTitle,
				bannerBody: bannerBody,
				showBannerCloseButton: STATIC_SETTINGS.showBannerCloseButton,
				showRemindLater: showRemindLater,
				showDismiss: showDismiss,
				updateNowLabel: STATIC_SETTINGS.updateNowLabel,
				remindLaterLabel: STATIC_SETTINGS.remindLaterLabel,
				dismissLabel: STATIC_SETTINGS.dismissLabel,
				appleStoreUrl: appleStoreUrl,
				googlePlayUrl: googlePlayUrl,
				amazonStoreUrl: amazonStoreUrl,
				revision: STATIC_SETTINGS.revision,
			},
		};

		setIsSubmitting(true);
		setShowModal(true);
		dispatch(releaseNotificationAction.releaseNotificationUpdateSetting(data));
		setFormSubmit(true);
	};

	const handleConfirmCancel = (e) => {
		e.preventDefault();
		e.stopPropagation();
		history.push("/");
	};

	return (
		<>
			<div
				className="release-notification-detail mb-5"
				data-testid="releaseNotificationPage"
			>
				{showModal && (
					<LoadingSpinnerModal isShowModal={showModal} modalChange={() => {}} />
				)}
				<div className="card-box m-0">
					<div className="card-box-link-back-div header-back">
						<FontAwesomeIcon icon={faChevronLeft} />
						<CardLink
							onClick={() => history.push("/")}
							className="card-box-link-back"
							data-testid="releaseNotification-backtolist"
						>
							Back to List
						</CardLink>
					</div>
					<form
						name="updateform"
						onSubmit={handleSubmit}
						className="create-form"
						data-testid="releaseNotificationForm"
					>
						<div className="form-section" data-testid="basicConfigSection">
							<div
								className="form-section-title"
								data-testid="basicConfigTitle"
							>
								Basic Configuration
							</div>
							<div className="form-section-body">
								<div className="row mb-3">
									<div className="col-md-3">
										<Label data-testid="modalEnabled" className="d-block pt-0">
											Modal Status
										</Label>
										<FormGroup switch>
											<Input
												type="switch"
												className="active-switch"
												id="modalEnabled"
												name="modalEnabled"
												data-testid="modalEnabledInput"
												checked={modalEnabled}
												onChange={() => {
													setModalEnabled(!modalEnabled);
												}}
											/>
											<Label check htmlFor="modalEnabled" className="ms-2">
												{modalEnabled ? "Enabled" : "Disabled"}
											</Label>
										</FormGroup>
									</div>
									<div className="col-md-3">
										<Label data-testid="bannerEnabled" className="d-block pt-0">
											Banner Status
										</Label>
										<FormGroup switch>
											<Input
												type="switch"
												className="active-switch"
												id="bannerEnabled"
												name="bannerEnabled"
												data-testid="bannerEnabledInput"
												checked={bannerEnabled}
												onChange={() => {
													handleBannerEnabledChange(!bannerEnabled);
												}}
											/>
											<Label check htmlFor="bannerEnabled" className="ms-2">
												{bannerEnabled ? "Enabled" : "Disabled"}
											</Label>
										</FormGroup>
									</div>
									<div className="col-md-6">
										<Label
											htmlFor="minAppVersion"
											data-testid="minAppVersion"
											className="pt-0"
										>
											Minimum App Version <span className="text-danger">*</span>
										</Label>
										<FormGroup>
											<Input
												type="text"
												id="minAppVersion"
												name="minAppVersion"
												data-testid="minAppVersionInput"
												required
												value={minAppVersion}
												onChange={(e) => {
													setMinAppVersion(e.target.value);
												}}
												placeholder="2.0.5"
												pattern=".*\S.*"
												title={WHITESPACE_ONLY_MESSAGE}
												maxLength={20}
											/>
											<small className="text-muted d-block mt-1">
												Enter the version in X.X.X format.
											</small>
											<small className="text-muted d-block mt-1">
												Users with this version or an older version will see
												the notification.
											</small>
											<small
												className="text-muted d-block mt-1"
												data-testid="minAppVersionNote"
											>
												The version must be greater than 2.0.4
											</small>
										</FormGroup>
									</div>
								</div>
								<div className="row">
									<div className="col-12">
										<Label
											data-testid="targetPlatforms"
											className="d-block pt-0"
										>
											Target Platforms <span className="text-danger">*</span>
										</Label>
										<FormGroup>
											<div
												className="d-flex flex-wrap platform-checkboxes"
												data-testid="targetPlatformsContainer"
											>
												<div className="form-check">
													<Input
														className="form-check-input"
														type="checkbox"
														id="allPlatforms"
														name="allPlatforms"
														data-testid="allPlatformsInput"
														checked={allPlatforms}
														onChange={() => {
															handleAllPlatformsChange(!allPlatforms);
														}}
													/>
													<Label
														className="form-check-label"
														htmlFor="allPlatforms"
													>
														All Platforms
													</Label>
												</div>
												<div className="form-check">
													<Input
														className="form-check-input"
														type="checkbox"
														id="targetApple"
														name="targetApple"
														data-testid="targetAppleInput"
														checked={targetApple}
														onChange={() => {
															handlePlatformChange(
																setTargetApple,
																!targetApple,
															);
														}}
													/>
													<Label
														className="form-check-label"
														htmlFor="targetApple"
													>
														Apple (iOS)
													</Label>
												</div>
												<div className="form-check">
													<Input
														className="form-check-input"
														type="checkbox"
														id="targetAndroid"
														name="targetAndroid"
														data-testid="targetAndroidInput"
														checked={targetAndroid}
														onChange={() => {
															handlePlatformChange(
																setTargetAndroid,
																!targetAndroid,
															);
														}}
													/>
													<Label
														className="form-check-label"
														htmlFor="targetAndroid"
													>
														Android
													</Label>
												</div>
												<div className="form-check">
													<Input
														className="form-check-input"
														type="checkbox"
														id="targetAmazon"
														name="targetAmazon"
														data-testid="targetAmazonInput"
														checked={targetAmazon}
														onChange={() => {
															handlePlatformChange(
																setTargetAmazon,
																!targetAmazon,
															);
														}}
													/>
													<Label
														className="form-check-label"
														htmlFor="targetAmazon"
													>
														Amazon
													</Label>
												</div>
											</div>
											{showPlatformError && (
												<div
													className="text-danger mt-2"
													data-testid="targetPlatformsError"
													role="alert"
												>
													At least one target device must be selected.
												</div>
											)}
										</FormGroup>
									</div>
								</div>
							</div>
						</div>

						<div
							className="form-section"
							data-testid="notificationContentSection"
						>
							<div
								className="form-section-title"
								data-testid="notificationContentTitle"
							>
								Notification Content
							</div>
							<div className="form-section-body">
								<div>
									<Label
										htmlFor="title"
										data-testid="modalTitle"
										className="pt-0"
									>
										Modal Title <span className="text-danger">*</span>
									</Label>
									<FormGroup>
										<DABTextBox
											type="text"
											id="title"
											data-testid="modalTitleInput"
											isRequired={true}
											isHover={false}
											onChange={(event) => {
												setTitle(event.target.value);
											}}
											value={title}
											pattern=".*\S.*"
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={TITLE_MAX_LENGTH}
										/>
										{title?.length >= TITLE_MAX_LENGTH && (
											<span className="text-warning">
												Character limit met. Maximum {TITLE_MAX_LENGTH}{" "}
												characters allowed
											</span>
										)}
									</FormGroup>
								</div>
								<div>
									<Label htmlFor="body" data-testid="modalBody">
										Modal Body <span className="text-danger">*</span>
									</Label>
									<FormGroup className="mb-0">
										<Input
											type="textarea"
											id="body"
											name="body"
											data-testid="modalBodyInput"
											innerRef={bodyRef}
											required
											rows={4}
											value={body}
											onChange={(e) => {
												setBody(e.target.value);
											}}
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={MODAL_BODY_MAX_LENGTH}
										/>
										<div
											className="text-end text-muted character-count"
											data-testid="modalBodyCount"
										>
											{body?.length ?? 0}/{MODAL_BODY_MAX_LENGTH}
										</div>
									</FormGroup>
								</div>
							</div>
						</div>

						<div className="form-section" data-testid="bannerContentSection">
							<div
								className="form-section-title"
								data-testid="bannerContentTitle"
							>
								Banner Content
							</div>
							<div className="form-section-body">
								<div>
									<Label
										htmlFor="bannerTitle"
										data-testid="bannerTitle"
										className="pt-0"
									>
										Banner Title <span className="text-danger">*</span>
									</Label>
									<FormGroup>
										<DABTextBox
											type="text"
											id="bannerTitle"
											data-testid="bannerTitleInput"
											isRequired={true}
											isHover={false}
											onChange={(event) => {
												setBannerTitle(event.target.value);
											}}
											value={bannerTitle}
											pattern=".*\S.*"
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={TITLE_MAX_LENGTH}
										/>
										{bannerTitle?.length >= TITLE_MAX_LENGTH && (
											<span className="text-warning">
												Character limit met. Maximum {TITLE_MAX_LENGTH}{" "}
												characters allowed
											</span>
										)}
									</FormGroup>
								</div>
								<div>
									<Label htmlFor="bannerBody" data-testid="bannerBody">
										Banner Body <span className="text-danger">*</span>
									</Label>
									<FormGroup className="mb-0">
										<Input
											type="textarea"
											id="bannerBody"
											name="bannerBody"
											data-testid="bannerBodyInput"
											innerRef={bannerBodyRef}
											required
											rows={3}
											value={bannerBody}
											onChange={(e) => {
												setBannerBody(e.target.value);
											}}
											title={WHITESPACE_ONLY_MESSAGE}
											maxLength={BANNER_BODY_MAX_LENGTH}
										/>
										<div
											className="text-end text-muted character-count"
											data-testid="bannerBodyCount"
										>
											{bannerBody?.length ?? 0}/{BANNER_BODY_MAX_LENGTH}
										</div>
									</FormGroup>
								</div>
							</div>
						</div>

						<div
							className="form-section"
							data-testid="notificationActionsSection"
						>
							<div
								className="form-section-title"
								data-testid="notificationActionsTitle"
							>
								Notification Actions
							</div>
							<div className="form-section-body">
								<div className="row mb-3">
									<div className="col-md-4">
										<FormGroup switch>
											<Input
												type="switch"
												className="active-switch"
												id="showRemindLater"
												name="showRemindLater"
												data-testid="showRemindLaterInput"
												checked={showRemindLater}
												onChange={() => {
													setShowRemindLater(!showRemindLater);
												}}
											/>
											<Label
												check
												htmlFor="showRemindLater"
												className="ms-2"
												data-testid="showRemindLater"
											>
												Show Remind Me Later
											</Label>
										</FormGroup>
									</div>
									<div className="col-md-4">
										<FormGroup switch>
											<Input
												type="switch"
												className="active-switch"
												id="showDismiss"
												name="showDismiss"
												data-testid="showDismissInput"
												checked={showDismiss}
												disabled={!bannerEnabled}
												onChange={() => {
													setShowDismiss(!showDismiss);
												}}
											/>
											<div className="d-flex flex-column">
												<Label
													check
													htmlFor="showDismiss"
													className={`ms-2 ${bannerEnabled ? "" : "text-muted"}`}
													data-testid="showDismiss"
												>
													Show Dismiss
												</Label>
												{!bannerEnabled && (
													<small
														className="text-muted ms-1"
														data-testid="showDismissHint"
													>
														(Enable the banner to use the dismiss option)
													</small>
												)}
											</div>
										</FormGroup>
									</div>
								</div>
								<div
									className="alert alert-primary mb-0 d-flex align-items-center"
									data-testid="forcedUpgradeNote"
								>
									<FontAwesomeIcon icon={faCircleInfo} className="me-2" />
									<span>
										Disable "Remind Me Later" and "Dismiss" to enforce a forced
										upgrade.
									</span>
								</div>
							</div>
						</div>

						<div className="form-section" data-testid="appStoreLinksSection">
							<div
								className="form-section-title"
								data-testid="appStoreLinksTitle"
							>
								App Store Links
							</div>
							<div className="form-section-body">
								<div className="row">
									<div className="col-md-4">
											<Label
												htmlFor="appleStoreUrl"
												data-testid="appleStoreUrl"
												className="pt-0"
											>
												Apple App Store URL{" "}
												{targetApple && <span className="text-danger">*</span>}
											</Label>
										<FormGroup className="mb-0">
											<Input
												type="url"
												id="appleStoreUrl"
												name="appleStoreUrl"
												data-testid="appleStoreUrlInput"
												required={targetApple}
												value={appleStoreUrl}
												onChange={(e) => {
													setAppleStoreUrl(e.target.value);
												}}
												placeholder="https://apps.apple.com/..."
												title="Please enter a valid URL (including https://)"
												maxLength={STORE_URL_MAX_LENGTH}
											/>
											<small className="text-muted d-block mt-1">
												Used for iOS devices.
											</small>
										</FormGroup>
									</div>
									<div className="col-md-4">
											<Label
												htmlFor="googlePlayUrl"
												data-testid="googlePlayUrl"
												className="pt-0"
											>
												Google Play Store URL{" "}
												{targetAndroid && (
													<span className="text-danger">*</span>
												)}
											</Label>
										<FormGroup className="mb-0">
											<Input
												type="url"
												id="googlePlayUrl"
												name="googlePlayUrl"
												data-testid="googlePlayUrlInput"
												required={targetAndroid}
												value={googlePlayUrl}
												onChange={(e) => {
													setGooglePlayUrl(e.target.value);
												}}
												placeholder="https://play.google.com/..."
												title="Please enter a valid URL (including https://)"
												maxLength={STORE_URL_MAX_LENGTH}
											/>
											<small className="text-muted d-block mt-1">
												Used for Android devices.
											</small>
										</FormGroup>
									</div>
									<div className="col-md-4">
											<Label
												htmlFor="amazonStoreUrl"
												data-testid="amazonStoreUrl"
												className="pt-0"
											>
												Amazon Appstore URL{" "}
												{targetAmazon && <span className="text-danger">*</span>}
											</Label>
										<FormGroup className="mb-0">
											<Input
												type="url"
												id="amazonStoreUrl"
												name="amazonStoreUrl"
												data-testid="amazonStoreUrlInput"
												required={targetAmazon}
												value={amazonStoreUrl}
												onChange={(e) => {
													setAmazonStoreUrl(e.target.value);
												}}
												placeholder="https://amazon.com/..."
												title="Please enter a valid URL (including https://)"
												maxLength={STORE_URL_MAX_LENGTH}
											/>
											<small className="text-muted d-block mt-1">
												Used for Amazon devices.
											</small>
										</FormGroup>
									</div>
								</div>
							</div>
						</div>

						<div className="row">
							<div className="col-12 d-flex justify-content-between align-items-center mt-2">
								<div data-testid="cancelButtonContainer">
									<DABButton
										id="cancel"
										className={`${isSubmitting ? "disabled" : ""}`}
										onClick={handleConfirmCancel}
										value="CANCEL"
									>
										CANCEL
									</DABButton>
								</div>
								<div
									className="d-flex align-items-center gap-2"
									data-testid="saveButtonContainer"
								>
									<Button
										type="submit"
										id="submit"
										color="primary"
										className=""
										data-testid="saveButton"
										disabled={isSubmitting}
									>
										{isSubmitting && <Spinner size="sm"></Spinner>} SAVE
									</Button>
								</div>
							</div>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state: any) => {
	const { releaseNotificationData } = state;
	return {
		releaseNotificationData,
	};
};

export default connect(mapStateToProps)(ReleaseNotificationDetail);
